var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { LIVE_LESSON_WATCHING_RECORD_SYNC_INTERVAL, StreakToastMessageMap, } from '@lean-body/src/domain';
import { LiveArchiveVideo } from './live_archive_video';
import { Vimeo } from './vimeo';
import { LiveLessonThumbnail } from '../molecules';
import { APISequences } from '@lean-body/src/interfaces';
import dayjs from 'dayjs';
import { css, StyleSheet } from 'aphrodite';
import { MediaBreakPointUp } from '../styles';
import { useToast } from '../hooks/use_toast';
import { useStreakCompletionResult } from '../hooks/use_streak_completion_result';
import { isiOSApp } from '@lean-body/src/util';
export const LiveVideo = ({ liveLesson, apiClient, signedCookieService, tracker, me }) => {
    const liveLessonWatchingTime = React.useRef(0); // 視聴時間のカウントアップ
    const [liveLessonWatchingRecordMap, setLiveLessonWatchingRecordMap] = React.useState(new Map()); // 重複排除視聴時間計測用map
    // Videojs, Vimeoのイベントハンドラーの中だと useState は更新されてなかったので useRef を使っています
    const previousPlaybackPosition = React.useRef(0);
    const useLessonSessionId = React.useRef(0);
    const src = liveLesson.isVimeoExpired ? liveLesson.videoSrc : liveLesson.playerUrl;
    const isBeingHeld = liveLesson.isBeingHeld();
    const [isExited, setIsExited] = React.useState(false);
    const { enqueuePendingToast } = useToast();
    const { setStreakCompletionResult } = useStreakCompletionResult();
    const checkDuringLesson = (currentPlaybackPosition) => {
        if (liveLesson.isFinished) {
            // アーカイブ再生中は現在の再生位置(currentPlaybackPosition)を計算に使用して、レッスンが行われてない時間はアクティビティに反映させない仕様
            const lessonStartSecond = liveLesson.startsAt - liveLesson.liveStartAt; // レッスンがライブ配信の何秒目に始まるか
            const lessonEndSecond = liveLesson.lessonEndAt - liveLesson.liveStartAt; // レッスンがライブ配信の何秒目に終わるか
            return lessonStartSecond < currentPlaybackPosition && currentPlaybackPosition < lessonEndSecond;
        }
        else {
            // ライブ中は現在時刻から、レッスンが行われてない時間はアクティビティに反映させない仕様
            return isBeingHeld;
        }
    };
    const onInit = () => {
        if (!liveLesson.isStartedAndWatchable)
            return;
        Promise.all([
            apiClient.startLiveLessonWatchingRecord(liveLesson.id),
            !isiOSApp() && apiClient.createStreakFromLiveLesson(liveLesson.id),
        ]).then(([session, streakCompletionResult]) => {
            useLessonSessionId.current = session.id;
            if (streakCompletionResult) {
                if (streakCompletionResult.isFirstDailyCompletion) {
                    setStreakCompletionResult(streakCompletionResult);
                }
                const toastMessage = StreakToastMessageMap[streakCompletionResult.sourceType];
                enqueuePendingToast(toastMessage);
            }
        });
    };
    const onPlay = (seconds) => {
        // Vimeoで登録されるレッスンの名前形式でtrackerに記録する
        const title = dayjs.unix(liveLesson.startsAt).format('MM/DD H:mm') + '-' + liveLesson.trainer.name;
        tracker.trackPlayLiveVideo(src, title, seconds);
        tracker.trackStartLiveLesson(me.id, liveLesson.id, src);
    };
    const onTimeupdate = (seconds) => {
        const currentPlaybackPosition = Math.floor(seconds); // 現在再生している位置（ex: 5秒目）
        const isDuringLesson = checkDuringLesson(currentPlaybackPosition);
        if (isDuringLesson) {
            const session = currentPlaybackPosition - previousPlaybackPosition.current;
            // 250ms毎に実行されるので、視聴時間のカウントアップを1秒ごとに行いたいため、前回の再生位置から1秒経過した時のみカウントアップする
            if (session === 1) {
                liveLessonWatchingTime.current += session;
                liveLessonWatchingRecordMap.set(currentPlaybackPosition, true); // 再生された箇所を記録したいだけなので、mapのvalueはtrueでなくても構わない
                setLiveLessonWatchingRecordMap(liveLessonWatchingRecordMap);
                if (liveLessonWatchingTime.current % LIVE_LESSON_WATCHING_RECORD_SYNC_INTERVAL === 0) {
                    apiClient.syncLiveLessonWatchingRecord(liveLesson.id, liveLessonWatchingTime.current, liveLessonWatchingRecordMap.size, useLessonSessionId.current);
                }
            }
        }
        previousPlaybackPosition.current = currentPlaybackPosition;
    };
    // リブレッスン視聴履歴をライブレスん終了時に保存する
    const logLessonRecord = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!liveLesson.isShowVideo) {
            return;
        }
        if (liveLessonWatchingRecordMap.size !== 0) {
            return apiClient
                .finishLiveLessonWatchingRecord(liveLesson.id, liveLessonWatchingTime.current, liveLessonWatchingRecordMap.size, useLessonSessionId.current)
                .then((session) => {
                tracker.trackQuitLiveLesson(me.id, liveLesson.id, src, session);
            })
                .finally(() => {
                // Web, iOS側で画面遷移・離脱した時、必ず window.finishLiveLessonWatchingRecord を呼び出しているので、
                // そのタイミングにプレーヤを破棄する
                setIsExited(true);
            });
        }
    });
    // コンポーネント離脱時のライブレッスン視聴履歴保存
    const cleanup = () => {
        apiClient.appendToSequence(APISequences.LESSON_FINISH_AND_PATCH_PROFILE).then((next) => __awaiter(void 0, void 0, void 0, function* () {
            yield logLessonRecord();
            next();
        }));
    };
    React.useEffect(() => {
        return cleanup;
    }, []);
    // ブラウザ閉じる時のライブレッスン視聴履歴保存
    window.onbeforeunload = () => {
        logLessonRecord();
    };
    // iOSがonbeforeunloadやReactのunmountを検知できないので、globalにmethodを定義しています
    window.finishLiveLessonWatchingRecord = () => {
        // ネイティブ側が完了を待てるようにPromiseを返す（keepaliveと併せて送信の確実性を高める）
        return logLessonRecord();
    };
    return (!isExited && (React.createElement("div", { className: css(styles.container) }, liveLesson.isShowVideo ? (liveLesson.isVimeoExpired ? (React.createElement(LiveArchiveVideo, { liveLesson: liveLesson, onInit: onInit, onPlay: onPlay, onTimeupdate: onTimeupdate, signedCookieService: signedCookieService })) : (React.createElement(Vimeo, { liveLesson: liveLesson, onInit: onInit, onPlay: onPlay, onTimeupdate: onTimeupdate }))) : (React.createElement(LiveLessonThumbnail, { src: liveLesson.bannerPcURL, isWatchable: liveLesson.isWatchable, isArchiveTranscodingComplete: liveLesson.isArchiveTranscodingComplete })))));
};
const styles = StyleSheet.create({
    container: {
        [MediaBreakPointUp.XL]: {
            marginBottom: 24,
        },
    },
});
